import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {FormControl, FormField} from 'platform/components';
import {Box, Grid, Heading, Show, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useEffect, useState} from 'react';
import {FieldPathValue, UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil, sort} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {getSafely, noop, suffixTestId, TestIdProps} from 'shared/web';

import {useCatalogue} from '../../../hooks/useCatalogue';
import {usePriceReport} from '../../../hooks/usePriceReport';
import i18n from '../../../i18n/i18n';
import {useGetApiCountriesQuery, useGetTrimLevelsQuery} from '../../../store/catalogueApi';
import {DrillDownLevelName, DrillDownStatisticsLevel} from '../../../types/api/drillDown';
import {featureFlags} from '../../../utils/featureFlags';
import {getYearOptions} from '../../../utils/getYearOptions';
import {EXCLUDED_FUEL_TYPES} from '../constants/excludedFuelTypes';
import {MAX_SEAT_COUNT} from '../constants/maxSeatCount';
import {WITHOUT_VAT} from '../constants/withoutVAT';
import {WITH_VAT} from '../constants/withVAT';
import {
  useFilterRelevantOptions,
  useGetRelevantPowerOptions,
} from '../hooks/useFilterRelevantOptions';
import {VehicleFormType} from '../types/VehicleFormType';
import {getFiltersFromFormData} from '../utils/getFiltersFromFormData';
import {DrillDownPicker} from './DrillDownPicker';
import {MakeModelFields} from './MakeModelFields';
import {PowerFields} from './PowerFields';
import {SeparatorWithLabel} from './SeparatorWithLabel';
import {VinParser} from './VinParser';

interface VehicleFieldsProps extends TestIdProps {
  control: FormControl<VehicleFormType>;
  formApi: UseFormReturn<VehicleFormType>;
}

const seatCountOptions = Array.from(Array(MAX_SEAT_COUNT)).map((_, index) => ({
  value: `${index + 1}`,
  label: `${index + 1}`,
}));

export function VehicleFields(props: VehicleFieldsProps) {
  const [pendingVinValidation, setPendingVinValidation] = useState(false);
  const {
    fuelOptions: allFuelOptions,
    styleOptions,
    transmissionOptions,
    driveOptions,
    colorOptions,
    doorOptions,
    interiorMaterialOptions,
  } = useCatalogue();
  const {requestData} = usePriceReport();

  const allowedFuelOptions = allFuelOptions?.filter(
    (option) => !EXCLUDED_FUEL_TYPES.includes(option.value)
  );
  const firstRegistrationYearOptions = getYearOptions();

  const make = props.formApi.watch('make');
  const modelFamily = props.formApi.watch('modelFamily');
  const firstRegistrationOnYear = props.formApi.watch('firstRegistrationOnYear');
  const mileage = props.formApi.watch('mileage');
  const fuelType = props.formApi.watch('fuelType');
  const transmission = props.formApi.watch('transmission');
  const drive = props.formApi.watch('drive');
  const power = props.formApi.watch('power');
  const bodyStyle = props.formApi.watch('bodyStyle');

  const errors = props.formApi.formState.errors;
  const hasBasicInfo =
    isNotNilOrEmpty(make) &&
    isNotNilOrEmpty(modelFamily) &&
    isNotNil(firstRegistrationOnYear) &&
    isNotNil(mileage);
  const hasFuelType = isNotNilOrEmpty(fuelType);
  const hasTransmission = isNotNilOrEmpty(transmission);
  const hasDrive = isNotNilOrEmpty(drive);
  const hasPower = isNotNil(power);
  const hasBodyStyle = isNotNilOrEmpty(bodyStyle);

  const isProgressiveDisclosure = isFeatureEnabled(featureFlags.PR_FORM_STATISTICS);
  const showFuelTypeSection = !isProgressiveDisclosure || hasBasicInfo || isNotNil(errors.fuelType);
  const showTransmissionSection =
    !isProgressiveDisclosure ||
    (showFuelTypeSection && hasFuelType) ||
    isNotNil(errors.transmission);
  const showDriveSection =
    !isProgressiveDisclosure ||
    (showTransmissionSection && hasTransmission) ||
    isNotNil(errors.drive);
  const showPowerSection =
    !isProgressiveDisclosure || (showDriveSection && hasDrive) || isNotNil(errors.power);
  const showBodyStyleSection =
    !isProgressiveDisclosure || (showPowerSection && hasPower) || isNotNil(errors.bodyStyle);
  const hasAdditionalSectionError =
    isNotNil(errors.doorCount) ||
    isNotNil(errors.seatCount) ||
    isNotNil(errors.isVatDeductible) ||
    isNotNil(errors.exteriorColor) ||
    isNotNil(errors.interiorMaterial);
  const showAdditionalSection =
    !isProgressiveDisclosure || (showBodyStyleSection && hasBodyStyle) || hasAdditionalSectionError;

  const {data: apiCountries, isLoading: isApiCountriesLoading} = useGetApiCountriesQuery({
    format: 'iso3',
  });
  const {currentData: trimLevels, isFetching: isTrimLevelsLoading} = useGetTrimLevelsQuery(
    isNil(make) || isNil(modelFamily)
      ? skipToken
      : {
          modelFamily,
          make,
          country: isNotNil(requestData?.market)
            ? getSafely(apiCountries, requestData.market)
            : undefined,
          yearFrom: firstRegistrationOnYear ?? undefined,
          yearTo: firstRegistrationOnYear ?? undefined,
        }
  );

  const hasTrimLevels =
    !isApiCountriesLoading && !isTrimLevelsLoading && isNotNilOrEmpty(trimLevels?.trim_levels);
  const trimLevelsOptions = sort(
    (a, b) => {
      if (isNotNil(a.label) && isNotNil(b.label)) {
        return a.label.localeCompare(b.label);
      }
      return 0;
    },
    trimLevels?.trim_levels?.map((trimLevel) => ({
      label: trimLevel.label,
      value: trimLevel.const_key,
    })) ?? []
  );

  useEffect(() => {
    if (
      isNotNil(trimLevels) &&
      !trimLevels.trim_levels
        ?.map((trimLevel) => trimLevel.const_key)
        .includes(props.formApi.getValues('trimLevel'))
    ) {
      props.formApi.setValue('trimLevel', '');
    }
  }, [trimLevels, props.formApi]);

  const values = props.formApi.getValues();
  const {options: transmissionOptionsFiltered, isLoading: isTransmissionOptionsLoading} =
    useFilterRelevantOptions(
      transmissionOptions ?? [],
      'transmission',
      getFiltersFromFormData(values, 'transmission')
    );
  const {options: fuelTypeOptionsFiltered, isLoading: isFuelTypeOptionsLoading} =
    useFilterRelevantOptions(
      allowedFuelOptions ?? [],
      'fuel_type',
      getFiltersFromFormData(values, 'fuel_type')
    );
  const {options: bodyStyleOptionsFiltered, isLoading: isBodyStyleOptionsLoading} =
    useFilterRelevantOptions(
      styleOptions ?? [],
      'car_style',
      getFiltersFromFormData(values, 'car_style')
    );
  const {options: driveOptionsFiltered, isLoading: isDriveOptionsLoading} =
    useFilterRelevantOptions(driveOptions ?? [], 'drive', getFiltersFromFormData(values, 'drive'));
  const {options: powerOptions, isLoading: isPowerOptionsLoading} = useGetRelevantPowerOptions(
    getFiltersFromFormData(values, 'power')
  );
  const isAnyOptionsLoading =
    isFuelTypeOptionsLoading ||
    isTransmissionOptionsLoading ||
    isBodyStyleOptionsLoading ||
    isDriveOptionsLoading ||
    isPowerOptionsLoading;

  const onFilterChange = useCallback(
    (
      name: keyof VehicleFormType,
      value: FieldPathValue<VehicleFormType, keyof VehicleFormType>
    ) => {
      props.formApi.setValue(name, value, {shouldTouch: true});
      props.control.onChange();
    },
    [props.formApi, props.control]
  );
  const onDrillDownRowSelect = (levels: DrillDownStatisticsLevel[]) => {
    levels.forEach((level) => {
      match<DrillDownLevelName, void>(level.name)
        .with('year', () => props.formApi.setValue('firstRegistrationOnYear', Number(level.value)))
        .with('fuel_type', () => props.formApi.setValue('fuelType', [level.value]))
        .with('transmission', () => props.formApi.setValue('transmission', [level.value]))
        .with('drive', () => props.formApi.setValue('drive', [level.value]))
        .with('power', () => props.formApi.setValue('power', Number(level.value)))
        .with('car_style', () => props.formApi.setValue('bodyStyle', [level.value]))
        .otherwise(noop);
    });
    props.control.onChange();
  };

  useEffect(() => {
    if (fuelTypeOptionsFiltered.length === 1 && !hasFuelType) {
      const onlyValue = fuelTypeOptionsFiltered[0].value;
      onFilterChange('fuelType', [onlyValue]);
    }
  }, [fuelTypeOptionsFiltered, hasFuelType, onFilterChange]);

  useEffect(() => {
    if (transmissionOptionsFiltered.length === 1 && !hasTransmission) {
      const onlyValue = transmissionOptionsFiltered[0].value;
      onFilterChange('transmission', [onlyValue]);
    }
  }, [transmissionOptionsFiltered, hasTransmission, onFilterChange]);

  useEffect(() => {
    if (driveOptionsFiltered.length === 1 && !hasDrive) {
      const onlyValue = driveOptionsFiltered[0].value;
      onFilterChange('drive', [onlyValue]);
    }
  }, [driveOptionsFiltered, hasDrive, onFilterChange]);

  useEffect(() => {
    if (bodyStyleOptionsFiltered.length === 1 && !hasBodyStyle) {
      const onlyValue = bodyStyleOptionsFiltered[0].value;
      onFilterChange('bodyStyle', [onlyValue]);
    }
  }, [bodyStyleOptionsFiltered, hasBodyStyle, onFilterChange]);

  useEffect(() => {
    if (powerOptions.length === 1 && !hasPower) {
      const onlyValue = Number(powerOptions[0].value);
      onFilterChange('power', onlyValue);
    }
  }, [powerOptions, hasPower, onFilterChange]);

  useEffect(() => {
    if (pendingVinValidation && !isAnyOptionsLoading) {
      props.formApi.trigger();
      setPendingVinValidation(false);
    }
  }, [pendingVinValidation, isAnyOptionsLoading, props.formApi]);

  return (
    <VStack spacing={4}>
      <Heading size={2}>{i18n.t('page.create.labels.vehicleSpecification')}</Heading>
      <VinParser
        control={props.control}
        formApi={props.formApi}
        onVinDecodeSuccess={() => setPendingVinValidation(true)}
        data-testid={suffixTestId('vinParser', props)}
      />
      <SeparatorWithLabel label={i18n.t('page.create.labels.basicInformation')} />
      <MakeModelFields
        control={props.control}
        formApi={props.formApi}
        data-testid={suffixTestId('makeModelFields', props)}
      />
      <Grid columns={[1, 1, 2]} spacing={6}>
        <FormField
          control={props.control}
          type="choice"
          name="firstRegistrationOnYear"
          label={i18n.t('page.create.labels.firstRegistrationOnYear')}
          isRequired
          options={firstRegistrationYearOptions}
          data-testid={suffixTestId('form-firstRegistrationOnYear', props)}
        />
        <FormField
          control={props.control}
          type="choice"
          name="trimLevel"
          label={i18n.t('page.create.labels.trimLevel')}
          options={trimLevelsOptions}
          isDisabled={!hasTrimLevels}
          data-testid={suffixTestId('form-trimLevel', props)}
        />
      </Grid>
      <Grid columns={[1, 1, 2]} spacing={6}>
        <Box flex={1}>
          <FormField
            control={props.control}
            type="number"
            name="mileage"
            label={`${i18n.t('page.create.labels.mileage')} (${i18n.t('general.metric.km')})`}
            isRequired
            data-testid={suffixTestId('form-mileage', props)}
          />
        </Box>
        <Show onDesktop onNotebook>
          <Space fillAvailable />
        </Show>
      </Grid>
      <Show when={showFuelTypeSection}>
        <SeparatorWithLabel label={i18n.t('page.create.labels.detailedInformation')} />
        <FormField
          control={props.control}
          type="chips"
          name="fuelType"
          label={i18n.t('page.create.labels.fuelType')}
          isRequired
          isDeselectable={fuelTypeOptionsFiltered.length > 1}
          options={fuelTypeOptionsFiltered}
          data-testid={suffixTestId('form-fuelType', props)}
        />
      </Show>
      <Show when={showTransmissionSection}>
        <FormField
          control={props.control}
          type="chips"
          name="transmission"
          label={i18n.t('page.create.labels.transmission')}
          isRequired
          isDeselectable={transmissionOptionsFiltered.length > 1}
          options={transmissionOptionsFiltered}
          data-testid={suffixTestId('form-transmission', props)}
        />
      </Show>
      <Show when={showDriveSection}>
        <FormField
          control={props.control}
          type="chips"
          name="drive"
          label={i18n.t('page.create.labels.drive')}
          isRequired
          isDeselectable={driveOptionsFiltered.length > 1}
          options={driveOptionsFiltered}
          data-testid={suffixTestId('form-drive', props)}
        />
      </Show>
      <Show when={showPowerSection}>
        <PowerFields
          power={power}
          fuelType={fuelType?.[0]}
          powerOptions={powerOptions}
          control={props.control}
          isManualEnterDisabled={isFeatureEnabled(featureFlags.PR_FORM_STATISTICS)}
          onFilterChange={onFilterChange}
          data-testid={suffixTestId('form-power', props)}
        />
      </Show>
      <Show when={showBodyStyleSection}>
        <FormField
          control={props.control}
          type="chips"
          name="bodyStyle"
          label={i18n.t('page.create.labels.bodyStyle')}
          isRequired
          isDeselectable={bodyStyleOptionsFiltered.length > 1}
          options={bodyStyleOptionsFiltered}
          data-testid={suffixTestId('form-bodyStyle', props)}
        />
      </Show>
      <Show whenFeatureEnabled={featureFlags.PR_FORM_STATISTICS} when={showFuelTypeSection}>
        <DrillDownPicker
          filters={getFiltersFromFormData(values)}
          onRowSelect={onDrillDownRowSelect}
          data-testid={suffixTestId('createDrillDown', props)}
        />
      </Show>
      <Show when={showAdditionalSection}>
        <SeparatorWithLabel label={i18n.t('page.create.labels.additionalInformation')} />
        <FormField
          control={props.control}
          type="chips"
          name="doorCount"
          label={i18n.t('page.create.labels.doorCount')}
          options={doorOptions ?? []}
          isDeselectable
          data-testid={suffixTestId('form-doorCount', props)}
        />
        <FormField
          control={props.control}
          type="chips"
          name="seatCount"
          label={i18n.t('page.create.labels.seatCount')}
          options={seatCountOptions}
          isDeselectable
          data-testid={suffixTestId('form-seatCount', props)}
        />
        <FormField
          control={props.control}
          type="chips"
          name="isVatDeductible"
          label={i18n.t('page.create.labels.vatDeductible')}
          options={[
            {label: i18n.t('page.create.labels.yes'), value: WITH_VAT},
            {label: i18n.t('page.create.labels.no'), value: WITHOUT_VAT},
          ]}
          isDeselectable
          data-testid={suffixTestId('form-isVatDeductible', props)}
        />
        <FormField
          control={props.control}
          type="chips"
          name="exteriorColor"
          label={i18n.t('page.create.labels.exteriorColor')}
          options={colorOptions ?? []}
          isDeselectable
          data-testid={suffixTestId('form-exteriorColor', props)}
        />
        <FormField
          control={props.control}
          type="chips"
          name="interiorMaterial"
          label={i18n.t('page.create.labels.interiorMaterial')}
          options={interiorMaterialOptions ?? []}
          isDeselectable
          data-testid={suffixTestId('form-interiorMaterial', props)}
        />
      </Show>
    </VStack>
  );
}
